import { z } from 'zod'
import { auditFieldsSchema, contactEmailSchema, contactPhoneSchema, uuidSchema } from './common.ts'
import { createPrimaryContactInputSchema } from './contacts.ts'

export const CLIENT_INDUSTRIES = [
  'automotive',
  'machinery',
  'construction',
  'agriculture',
  'other',
] as const

export const clientIndustrySchema = z.enum(CLIENT_INDUSTRIES)
export type ClientIndustry = z.infer<typeof clientIndustrySchema>

export const CLIENT_INDUSTRY_LABELS: Record<ClientIndustry, string> = {
  automotive: 'Автомобільна промисловість / OEM',
  machinery: 'Машинобудування',
  construction: 'Будівництво',
  agriculture: 'Сільське господарство',
  other: 'Інше',
}

/**
 * A company, a sole trader (ФОП) or a private person. Stored, not inferred: an 8-digit ЄДРПОУ
 * implies a company and a 10-digit ІПН a ФОП, but a private person who buys once has no code.
 */
export const CLIENT_KINDS = ['company', 'fop', 'person'] as const
export const clientKindSchema = z.enum(CLIENT_KINDS)
export type ClientKind = z.infer<typeof clientKindSchema>

export const CLIENT_KIND_LABELS: Record<ClientKind, string> = {
  company: 'Компанія',
  fop: 'ФОП',
  person: 'Приватна особа',
}

const nullableText = z.string().trim().min(1).nullable()

export const CLIENT_STATUSES = ['lead', 'active', 'inactive'] as const
export const clientStatusSchema = z.enum(CLIENT_STATUSES)
export type ClientStatus = z.infer<typeof clientStatusSchema>

export const CLIENT_STATUS_LABELS: Record<ClientStatus, string> = {
  lead: 'Потенційний',
  active: 'Активний',
  inactive: 'Неактивний',
}

export const clientSchema = z
  .object({
    id: uuidSchema,
    name: z.string().min(1),
    kind: clientKindSchema,
    industry: clientIndustrySchema,
    edrpou: z
      .string()
      .regex(/^(\d{8}|\d{10})$/)
      .nullable(),
    ownerId: uuidSchema.nullable(),
    status: clientStatusSchema,
    website: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    address: z.string().nullable(),
    city: z.string().nullable(),
    note: z.string().nullable(),
    defaultSellerEntityId: uuidSchema.nullable(),
    /** Our companies that work with this client; always includes the default one. */
    sellerEntityIds: z.array(uuidSchema),
  })
  .extend(auditFieldsSchema.shape)

export type Client = z.infer<typeof clientSchema>

export const createClientInputSchema = z.object({
  name: z.string().trim().min(1).max(200),
  /** Omitted: inferred from the code, 10 digits being a ФОП. */
  kind: clientKindSchema.optional(),
  industry: clientIndustrySchema,
  edrpou: z
    .string()
    .trim()
    .regex(/^(\d{8}|\d{10})$/, 'ЄДРПОУ має містити 8 або 10 цифр')
    .nullable(),
  /* Optional on creation: the quick-create dialog asks for a name and an industry, and the rest
     is filled in later from the company sheet. The database supplies `status`. */
  status: clientStatusSchema.optional(),
  website: nullableText.optional(),
  phone: contactPhoneSchema.nullable().optional(),
  email: contactEmailSchema.nullable().optional(),
  address: nullableText.optional(),
  city: nullableText.optional(),
  note: nullableText,
  defaultSellerEntityId: uuidSchema.nullable().optional(),
  sellerEntityIds: z.array(uuidSchema).max(50).optional(),
  primaryContact: createPrimaryContactInputSchema.nullable().optional(),
})

export type CreateClientInput = z.infer<typeof createClientInputSchema>

export const clientListQuerySchema = z.object({
  search: z.string().trim().max(100).optional(),
})

export type ClientListQuery = z.infer<typeof clientListQuerySchema>

export const updateClientInputSchema = createClientInputSchema
  .omit({ primaryContact: true })
  .extend({ ownerId: uuidSchema.nullable().optional() })
  .partial()
  .refine((input) => Object.keys(input).length > 0, 'At least one field must be updated')

export type UpdateClientInput = z.infer<typeof updateClientInputSchema>

export const kindForCode = (edrpou: string | null): ClientKind =>
  edrpou?.length === 10 ? 'fop' : 'company'

/**
 * The code a kind carries: a company's ЄДРПОУ has 8 digits (none for a foreign firm), a ФОП's
 * ІПН has 10, a private person has none. Checked on the merged record, so an update that changes
 * only one of the two is judged against the other's stored value.
 */
export const clientCodeIssue = (kind: ClientKind, edrpou: string | null): string | null => {
  if (edrpou === null) return null
  if (kind === 'person') return 'У приватної особи немає ЄДРПОУ чи ІПН'
  if (kind === 'fop' && edrpou.length !== 10) return 'ІПН ФОП має 10 цифр'
  if (kind === 'company' && edrpou.length !== 8) return 'ЄДРПОУ компанії має 8 цифр'
  return null
}
